import React, { useEffect, useRef, useState } from "react";
import { FaUsers, FaHandsHelping, FaHeart, FaGlobe } from "react-icons/fa";
import MyPage from "../components/MyPage";

/* ICON MAP (JSON key → icon) */
const iconMap = {
  users: <FaUsers />,
  hands: <FaHandsHelping />,
  heart: <FaHeart />,
  globe: <FaGlobe />
};

function CommunityEngagement() {
  const sectionRef = useRef(null);
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    /* ===== JSON (UI DATA) ===== */
    fetch("/api/community.json")
      .then(res => res.json())
      .then(json => setData(json))
      .catch(err => console.error("Community JSON error:", err));

    /* ===== XML (SEO / proof only) ===== */
    fetch("/api/community.xml").then(() => {});
  }, []);

  useEffect(() => {
    const banner = document.querySelector(".inner-banner");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        banner?.classList.add("animate");
      });
    });

    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current.classList.add("in-view");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [data]);

  if (!data) return null;

  return (
    <>
      {/* ===== SEO (NO API) ===== */}
      <MyPage
        title="Community Engagement | Thrissur Jilla Pravasi Kootayma"
        description="Thrissur Pravasi families united through community engagement and social welfare."
        keywords="Thrissur Pravasi, Community Engagement, Pravasi Activities"
      />

      {/* ===== INNER BANNER ===== */}
      <section className="inner-banner">
        <div className="inner-overlay"></div>

        <div className="inner-content">
          <h1>{data.banner.title}</h1>
        </div>

        <div className="breadcrumb">
          {data.banner.breadcrumb}
        </div>
      </section>

      {/* ===== COMMUNITY SECTION ===== */}
      <section className="community-section" ref={sectionRef}>
        <div className="community-header reveal-top">
          <h2>
            <FaUsers /> {data.header.title} <span></span>
          </h2>
          <p>{data.header.description}</p>
        </div>

        <div className="community-grid">
          {data.cards.map((item, index) => (
            <div
              className="community-card reveal-card"
              key={index}
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div className="community-icon">
                {iconMap[item.icon]}
              </div>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="community-images">
          {data.images.map((img, i) => (
            <div
              className="img-box"
              key={i}
              onClick={() => setActiveImage(img)}
            >
              <img src={img} alt={`Community ${i + 1}`} />
            </div>
          ))}
        </div>
      </section>
       {/* ===== IMAGE POPUP ===== */}
      {activeImage && (
        <div className="image-popup" onClick={() => setActiveImage(null)}>
          <img src={activeImage} alt="Preview" />
          <span className="close-btn">×</span>
        </div>
      )}


      {/* ===== CSS ===== */}
      <style>{`
/* ===== INNER BANNER ===== */
.inner-banner {
  position: relative;
  height: 420px;
  background: url("/assets/banner.jpg") center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  padding-top: 120px;
}

.inner-overlay {

  inset: 0;
  background: rgba(0,0,0,0.65);
}

.inner-content {
  position: relative;
  z-index: 1;
}

.inner-content h1 {
  font-size: 48px;
  font-weight: 700;
  font-family: "Merriweather", serif;
}

.breadcrumb {
  position: absolute;
  bottom: -20px;
  left: 55px;
  border-radius: 4px;
  background: var(--primary-color);
  padding: 10px 25px;
  font-size: 20px;
  font-weight: 500;
  color: #000;
  z-index: 1;
}
/* ===== COMMUNITY SECTION ===== */
.community-section {
  padding: 100px 80px;
  background: #fff;
  overflow-x: hidden;
}

.community-header {
  text-align: center;
  max-width: 850px;
  margin: 0 auto 70px;
}

/* HEADER ANIMATION */
.reveal-top {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s ease;
}

.community-header h2 {
  font-size: 36px;
  font-weight: 700;
  font-family: "Merriweather", serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.community-header h2 span {
  width: 60px;
  height: 2px;
  background: #000;
}

.community-header p {
  font-size: 16px;
  margin-top: 12px;
  color: #333;
  line-height: 1.7;
}

/* ===== GRID ===== */
.community-grid {
  max-width: 1300px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
}

/* ===== CARD ===== */
.community-card {
  background: #f7f9ee;
  padding: 35px 25px;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}

/* CARD ANIMATION */
.reveal-card {
  opacity: 0;
  transform: translateY(50px) scale(0.95);
  transition: all 0.9s ease;
}

/* ACTIVE */
.community-section.in-view .reveal-top,
.community-section.in-view .reveal-card {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.community-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
}

.community-icon {
  font-size: 42px;
  color: var(--primary-color);
  margin-bottom: 15px;
}

.community-card h4 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 10px;
  font-family: "Merriweather", serif;
}

.community-card p {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .community-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .community-section {
    padding: 70px 20px;
  }

  .community-grid {
    grid-template-columns: 1fr;
  }

  .inner-content h1 {
    font-size: 25px;
  }

  .community-header h2 {
    font-size: 28px;
  }

  .breadcrumb {
    left: 16px;
    bottom: -10px;
    padding: 8px 12px;
    font-size: 12px;
  }
}
  /* ===== INNER BANNER ANIMATION ===== */
.inner-content {
  opacity: 0;
  transform: translateY(-40px);
  transition: all 0.9s ease;
}

.breadcrumb {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.9s ease;
}

.inner-banner.animate .inner-content,
.inner-banner.animate .breadcrumb {
  opacity: 1;
  transform: translateY(0);
}

/* ===== COMMUNITY IMAGES GRID ===== */
.community-images {
  max-width: 1200px;
  margin: 80px auto 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
}

.img-box {
  cursor: pointer;
  overflow: hidden;
  border-radius: 12px;
}

.img-box img {
  width: 100%;
  height: 220px;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.img-box:hover img {
  transform: scale(1.08);
}

/* ===== IMAGE POPUP ===== */
.image-popup {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-popup img {
  max-width: 90%;
  max-height: 90%;
  border-radius: 12px;
  animation: zoomIn 0.4s ease;
}

.close-btn {
  position: absolute;
  top: 25px;
  right: 30px;
  font-size: 40px;
  color: #fff;
  cursor: pointer;
}

/* ===== POPUP ANIMATION ===== */
@keyframes zoomIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .community-images {
    grid-template-columns: repeat(2, 1fr);
  }

  .img-box img {
    height: 180px;
  }
}


      `}</style>
    </>
  );
}

export default CommunityEngagement;
